/*───────────────────────────────────────────────────────────────
 🌐 Project: Ring2All Platform
 🧑‍💻 Author: Rodrigo Cuadra
 🏢 Company: Ring2All LLC
 📄 File: FeatureCategoriesPage.tsx
 🗓️ Created: 2025-01-27
 🧩 Description:
    Main page for Feature Categories management with list view.

 🔧 Notes:
    - Refactored from monolithic file
    - Uses hooks for state management
    - Delegates data fetching to useFeatureCategoryList

 🔁 Last Updated: 2025-01-27 by Rodrigo Cuadra (Refactored)
────────────────────────────────────────────────────────────────*/

import { useState, useCallback, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useI18n } from '../../hooks/useI18n';
import { useNavigationStore } from '../../store/navigation';
import { useNotifications } from '../../contexts/NotificationContext';
import { useContextStore } from '../../store/context';
import DataGridSimple from '../../components/ui/DataGridSimple';
import ConfirmationModal from '../../components/ui/ConfirmationModal';
import { Edit, Trash2, Copy, Layers } from 'lucide-react';
import SimpleTooltip from '../../components/ui/SimpleTooltip';
import { useDataGrid } from '../../hooks/useDataGrid';
import { ModulePageContainer } from '../../components/layouts';
import { useFeatureCategoryList } from './hooks/useFeatureCategoryList';
import {
  deleteFeatureCategoryById,
  navigateToFeatureCategoryDuplicate,
} from './featureCategoriesActions';
import { FeatureCategory } from './types';

const EMPTY_PLACEHOLDER = '—';

export function FeatureCategoriesPage() {
  const { t } = useI18n();
  const { setCurrentPageTitle } = useNavigationStore();
  const navigate = useNavigate();
  const { showSuccess, showError } = useNotifications();
  const { selectedDomain } = useContextStore();
  const dateFormatter = useMemo(() => new Intl.DateTimeFormat(undefined), []);

  const pageTitle = useMemo(() => t('featureCategories.title'), [t]);

  useEffect(() => {
    setCurrentPageTitle(null);
    return () => setCurrentPageTitle(null);
  }, [setCurrentPageTitle]);

  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [categoryToDelete, setCategoryToDelete] = useState<FeatureCategory | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  const { fetchData } = useFeatureCategoryList({
    domainId: selectedDomain?.id,
  });

  const {
    data,
    pagination,
    sortBy,
    sortOrder,
    searchQuery,
    loading,
    columns,
    handlePageChange,
    handleItemsPerPageChange,
    handleSort,
    handleSearch,
    handleColumnToggle,
    refresh,
  } = useDataGrid({
    fetchData,
    initialColumns: [
      { key: 'name', label: t('featureCategories.categoryName'), sortable: true, visible: true },
      {
        key: 'description',
        label: t('featureCategories.categoryDescription'),
        sortable: false,
        visible: true,
      },
      {
        key: 'members',
        label: t('featureCategories.featureCount'),
        sortable: false,
        visible: true,
      },
      {
        key: 'createdAt',
        label: t('featureCategories.createdAt'),
        sortable: true,
        visible: false,
      },
      {
        key: 'updatedAt',
        label: t('featureCategories.updatedAt'),
        sortable: true,
        visible: false,
      },
      { key: 'actions', label: t('common.actions'), sortable: false, visible: true },
    ],
    initialSortBy: 'name',
    initialSortOrder: 'asc',
    initialLimit: 10,
    moduleName: 'feature-categories',
  });

  // Refresh data when domain changes
  useEffect(() => {
    if (selectedDomain?.id !== undefined) {
      refresh();
    }
  }, [selectedDomain?.id]);

  const handleCreate = useCallback(() => {
    navigate('/pbx/class-of-services/feature-categories/new');
  }, [navigate]);

  const handleEdit = useCallback(
    (category: FeatureCategory) => {
      navigate(`/pbx/class-of-services/feature-categories/${category.uuid || category.id}`);
    },
    [navigate]
  );

  const handleDuplicate = useCallback(
    (category: FeatureCategory) => {
      navigateToFeatureCategoryDuplicate(navigate, category.uuid || category.id);
    },
    [navigate]
  );

  const handleDeleteClick = useCallback((category: FeatureCategory) => {
    setCategoryToDelete(category);
    setShowDeleteModal(true);
  }, []);

  const handleDelete = useCallback(async () => {
    if (!categoryToDelete) return;

    setIsDeleting(true);
    try {
      await deleteFeatureCategoryById(categoryToDelete.id);

      showSuccess(t('featureCategories.messages.deleteSuccess'));
      setShowDeleteModal(false);
      setCategoryToDelete(null);
      refresh();
    } catch (error) {
      const message =
        error instanceof Error
          ? error.message
          : t('featureCategories.messages.deleteError');
      showError(message);
    } finally {
      setIsDeleting(false);
    }
  }, [categoryToDelete, showSuccess, showError, t, refresh]);

  const renderActions = useCallback(
    (category: FeatureCategory) => (
      <div className="flex items-center gap-2">
        <SimpleTooltip content={t('featureCategories.actions.edit')}>
          <button
            onClick={() => handleEdit(category)}
            className="p-1 rounded transition-colors text-blue-600 hover:text-blue-700 hover:bg-blue-100/60 dark:text-blue-300 dark:hover:text-blue-200 dark:hover:bg-blue-900/30"
          >
            <Edit className="h-4 w-4" />
          </button>
        </SimpleTooltip>

        <SimpleTooltip content={t('featureCategories.actions.duplicate')}>
          <button
            onClick={() => handleDuplicate(category)}
            className="p-1 rounded transition-colors text-green-600 hover:text-green-700 hover:bg-green-100/60 dark:text-green-300 dark:hover:text-green-200 dark:hover:bg-green-900/30"
          >
            <Copy className="h-4 w-4" />
          </button>
        </SimpleTooltip>

        <SimpleTooltip content={t('featureCategories.actions.delete')}>
          <button
            onClick={() => handleDeleteClick(category)}
            className="p-1 rounded transition-colors text-red-600 hover:text-red-700 hover:bg-red-100/60 dark:text-red-300 dark:hover:text-red-200 dark:hover:bg-red-900/30"
          >
            <Trash2 className="h-4 w-4" />
          </button>
        </SimpleTooltip>
      </div>
    ),
    [handleEdit, handleDuplicate, handleDeleteClick, t]
  );

  return (
    <ModulePageContainer
      currentView="list"
      listView={
        <>
          <DataGridSimple
            title={t('featureCategories.title')}
            titleIcon={<Layers className="h-5 w-5 text-primary" />}
            data={data}
            columns={columns.map((column) => {
              if (column.key === 'name') {
                return {
                  ...column,
                  render: (value: unknown) => (
                    <span className="font-medium text-foreground">{value as string}</span>
                  ),
                };
              }

              if (column.key === 'description') {
                return {
                  ...column,
                  render: (value: unknown) => (
                    <span className="text-sm text-muted-foreground truncate block max-w-[300px]">
                      {value ? String(value) : EMPTY_PLACEHOLDER}
                    </span>
                  ),
                };
              }

              if (column.key === 'members') {
                return {
                  ...column,
                  render: (_value: unknown, item: FeatureCategory) => (
                    <span className="text-sm text-center block">
                      {item._count?.members ?? 0}
                    </span>
                  ),
                };
              }

              if (column.key === 'createdAt' || column.key === 'updatedAt') {
                return {
                  ...column,
                  render: (value: unknown) => {
                    const date = value ? new Date(String(value)) : null;
                    return (
                      <span className="text-sm text-muted-foreground">
                        {date && !Number.isNaN(date.getTime())
                          ? dateFormatter.format(date)
                          : EMPTY_PLACEHOLDER}
                      </span>
                    );
                  },
                };
              }

              if (column.key === 'actions') {
                return {
                  ...column,
                  render: (_value: unknown, item: FeatureCategory) => renderActions(item),
                };
              }

              return column;
            })}
            pagination={pagination}
            onPageChange={handlePageChange}
            onItemsPerPageChange={handleItemsPerPageChange}
            onSort={handleSort}
            sortBy={sortBy}
            sortOrder={sortOrder}
            onSearch={handleSearch}
            searchQuery={searchQuery}
            loading={loading}
            onColumnToggle={handleColumnToggle}
            onAdd={handleCreate}
            addButtonText={t('featureCategories.empty.action')}
            renderActions={renderActions}
            emptyMessage={t('featureCategories.empty.description')}
            searchPlaceholder={t('featureCategories.searchCategories')}
          />

          <ConfirmationModal
            isOpen={showDeleteModal}
            onClose={() => {
              setShowDeleteModal(false);
              setCategoryToDelete(null);
            }}
            onConfirm={handleDelete}
            title={t('featureCategories.deleteCategory')}
            message={t('featureCategories.messages.confirmDelete')}
            confirmText={t('featureCategories.actions.delete')}
            cancelText={t('featureCategories.actions.cancel')}
            isLoading={isDeleting}
            type="danger"
          />
        </>
      }
      formView={null}
    />
  );
}

export default FeatureCategoriesPage;
